import { FC, useEffect, useRef, useState } from 'react';

import { $convertToMarkdownString } from '@lexical/markdown';
import { LexicalEditor } from 'lexical';
import { Controller, useForm } from 'react-hook-form';

import { useMailSequenceUpdateScheduledSendMutation } from '../../../../generated/graphql';

import { MAX_SUBJECT_LENGTH } from '../../FollowUpBuilder/helpers';
import { MARKDOWN_TRANSFORMERS } from '../../../components/Editor/plugins/MarkdownTransformers';
import Editor from '../../../components/Editor';
import InputGroup from '../../../components/InputGroup';
import ModalDialog from '../../../components/ModalDialog';
import Spinner from '../../../svgs/Spinner';
import { customToast } from '../../../components/ToastAlert/customToast';
import { UpcomingScheduledMail } from './types';

type Props = {
  isOpen: boolean;
  setClosed: () => void;
  scheduledMail: UpcomingScheduledMail;
  onSuccess: () => void;
};

interface EditFormData {
  subject: string;
}

const EditScheduledMailModal: FC<Props> = ({
  isOpen,
  setClosed,
  scheduledMail,
  onSuccess,
}) => {
  const editorRef = useRef<LexicalEditor | null>(null);
  const [providerNoteMarkdown, setProviderNoteMarkdown] = useState(
    scheduledMail.providerNote ?? '',
  );
  const [openCount, setOpenCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setProviderNoteMarkdown(scheduledMail.providerNote ?? '');
      setOpenCount((c) => c + 1);
    }
  }, [isOpen, scheduledMail.id, scheduledMail.providerNote]);

  const [updateMutation, { loading: isSubmitting }] =
    useMailSequenceUpdateScheduledSendMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditFormData>({
    defaultValues: {
      subject: scheduledMail.subject ?? '',
    },
    values: {
      subject: scheduledMail.subject ?? '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({ subject: scheduledMail.subject ?? '' });
    }
  }, [isOpen, scheduledMail.id, scheduledMail.subject, reset]);

  const onSubmit = async (data: EditFormData) => {
    const trimmedSubject = data.subject.trim();
    if (!trimmedSubject) {
      customToast.error('Subject is required.');
      return;
    }

    let providerNote = '';
    if (editorRef.current) {
      const editorState = editorRef.current.getEditorState();
      editorState.read(() => {
        providerNote = $convertToMarkdownString(
          MARKDOWN_TRANSFORMERS,
          undefined,
          true,
        );
      });
    } else {
      providerNote = providerNoteMarkdown || '';
    }

    const hasSubjectChange = trimmedSubject !== (scheduledMail.subject ?? '');
    const hasNoteChange = providerNote !== (scheduledMail.providerNote ?? '');

    if (!hasSubjectChange && !hasNoteChange) {
      customToast.error('No changes to save.');
      return;
    }

    try {
      const { data: result } = await updateMutation({
        variables: {
          input: {
            scheduledSendId: scheduledMail.id,
            subject: trimmedSubject,
            providerNote,
          },
        },
      });

      if (!result?.mailSequenceUpdateScheduledSend?.success) {
        customToast.error(
          result?.mailSequenceUpdateScheduledSend?.error ||
            'Unable to update mail.',
        );
        return;
      }

      customToast.success('Mail updated.');
      setClosed();
      onSuccess();
    } catch {
      customToast.error('Unable to update mail.');
    }
  };

  return (
    <ModalDialog
      title="Edit Mail"
      isOpen={isOpen}
      setClosed={setClosed}
      primaryActionTitle="Save"
      primaryActionOnClick={handleSubmit(onSubmit)}
      primaryActionDisabled={isSubmitting}
      fetching={isSubmitting}
      width="large"
      overflow="auto"
      fullHeight
    >
      <div className="flex flex-col gap-4">
        <div>
          <label
            htmlFor="subject"
            className="mb-2 block text-caption font-medium text-neutral-150"
          >
            Subject
          </label>
          <Controller
            name="subject"
            control={control}
            rules={{
              required: 'Subject is required',
              maxLength: {
                value: MAX_SUBJECT_LENGTH,
                message: `Subject must be less than ${MAX_SUBJECT_LENGTH} characters`,
              },
            }}
            render={({ field }) => (
              <InputGroup
                labelHidden
                label="subject"
                placeholder="Add a subject"
                errorMessage={errors.subject?.message}
                maxLength={MAX_SUBJECT_LENGTH}
                {...field}
              />
            )}
          />
        </div>
        <div>
          <label className="mb-2 block text-caption font-medium text-neutral-150">
            Message
          </label>
          <Editor
            innerRef={editorRef}
            readOnly={false}
            initialContentMarkdown={scheduledMail.providerNote ?? ''}
            elKey={`edit-scheduled-mail-${scheduledMail.id}-${openCount}`}
            onChange={(editorState) => {
              editorState.read(() => {
                const md = $convertToMarkdownString(
                  MARKDOWN_TRANSFORMERS,
                  undefined,
                  true,
                );
                setProviderNoteMarkdown(md);
              });
            }}
            className="prose prose-sm min-h-[200px] max-w-none"
            containerClassName="w-full"
          />
        </div>
      </div>
      {isSubmitting && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80">
          <Spinner className="h-8 w-8" />
        </div>
      )}
    </ModalDialog>
  );
};

export default EditScheduledMailModal;
